import { SHOWCASE_PARAM } from '@src/hooks/useShowcaseParamSync';
import { UNASSIGNED_FOLDER_ID } from '@src/stores-v2/FolderStructureStore';
import { useFolderStructure } from '@src/stores-v2/StoreContext';
import { Studio } from '@src/types.ts/general';
import { LogManager } from '@src/utility/Logger';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const logger = LogManager.getLogger('feature:story-manager-deep-link');

export const FOLDER_PARAM = 'folder';

/**
 * URL token for the virtual "Unassigned" grouping. Deliberately not the
 * `__unassigned__` sentinel — that shape reads as an internal id in a link
 * someone is about to paste into Slack.
 */
export const UNASSIGNED_PARAM = 'unassigned';

/** The `?folder=` value that represents a given `currentFolderId`. */
const paramForFolderId = (folderId: string | null): string | null => {
  if (folderId == null) return null;
  if (folderId === UNASSIGNED_FOLDER_ID) return UNASSIGNED_PARAM;
  return folderId;
};

/**
 * The absolute, shareable URL for a folder.
 *
 * Built from the folder's id rather than from `window.location`, because the
 * folder menus are available on folders the user is NOT standing in — a tile
 * in the grid, a row in the list. Copying the current address there would hand
 * over whatever folder happens to be open, which is right often enough to look
 * like it works and wrong often enough to matter.
 */
export function buildFolderShareLink(
  folderId: string,
  showcaseSlug?: string,
): string {
  const params = new URLSearchParams();
  params.set(FOLDER_PARAM, paramForFolderId(folderId) ?? '');
  if (showcaseSlug) params.set(SHOWCASE_PARAM, showcaseSlug);
  // The folder only exists in the Dato environment it was copied from.
  const env = new URLSearchParams(window.location.search).get('env');
  if (env) params.set('env', env);
  return `${window.location.origin}/${Studio.dashboard}?${params.toString()}`;
}

type Options = {
  showcaseId: string;
  /** Slug of the active showcase, carried so a shared link lands in the right workspace. */
  showcaseSlug?: string;
};

/**
 * Deep links for Story Manager: `?folder=<id>` (plus `?showcase=<slug>`) so a
 * folder — at any depth — can be shared, bookmarked, and survive a refresh.
 *
 * Folder ids are backend-assigned and stable across renames and moves, so the
 * link keeps working after someone reorganizes. Only the leaf is encoded: the
 * ancestor chain is derived from it by `breadcrumbForFolder`, and the tree
 * sidebar already auto-expands those ancestors when `currentFolderId` changes.
 *
 * The two effects below mirror in opposite directions and each compares before
 * writing, so they settle rather than loop (pitfall 1: renderer-style sync
 * loops). They can't be collapsed into a single URL-is-truth flow without
 * threading a writer through every `enterFolder` call site — including ones
 * outside this tree (the search dropdown, the upload flow's folder picker) —
 * so the mirror keeps those call sites untouched and still linkable.
 */
export function useFolderDeepLink({ showcaseId, showcaseSlug }: Options): {
  pending: boolean;
} {
  const store = useFolderStructure();
  const [searchParams, setSearchParams] = useSearchParams();
  const folderParam = searchParams.get(FOLDER_PARAM);
  // These two are read during RENDER so the host observer subscribes to them
  // and the effects below actually re-run when the store moves or the
  // structure lands. Both effects then re-read the store directly rather than
  // trusting these: the inbound effect mutates the store, and the outbound
  // effect runs in that same commit, where the render closure still holds the
  // pre-mutation value. Comparing against the stale value made the two effects
  // fight — each undoing the other's write, forever.
  const currentFolderId = store.currentFolderId;
  const hydrated = store.hydratedFor === showcaseId;

  // `setSearchParams` gets a fresh identity on every URL change, which would
  // re-fire these effects on unrelated param writes. Read it through a ref
  // (PLAT-11400 — see `useRedlineUrlState`).
  const write = useRef(setSearchParams);
  write.current = setSearchParams;

  // The showcase whose inbound link has been consumed. Gates the outbound
  // mirror: without it, the mount pass would strip an inbound `?folder=` back
  // out of the URL before the folder structure had finished loading.
  const reconciledFor = useRef<string | null>(null);
  // True for the single outbound write that follows reconciliation, which
  // records existing state rather than a navigation — so it replaces instead
  // of pushing a history entry the user never asked for.
  const mirroringExisting = useRef(false);
  // Mirrors `reconciledFor` as state so the host re-renders once the inbound
  // link is consumed, even when consuming it didn't move the store.
  const [consumedFor, setConsumedFor] = useState<string | null>(null);

  // ─── Inbound: URL → store ────────────────────────────────────────────────
  useEffect(() => {
    // `enterFolder` no-ops on an id it hasn't loaded yet, so nothing can be
    // resolved until the structure for this showcase has landed.
    if (!hydrated) return;

    const isFirstPass = reconciledFor.current !== showcaseId;
    reconciledFor.current = showcaseId;

    if (isFirstPass) {
      mirroringExisting.current = true;
      setConsumedFor(showcaseId);
      // An absent param on arrival means "unspecified", not "root": Story
      // Manager unmounts on navigation but the store remembers where the user
      // was, and that deliberately survives a round trip to Content Studio.
      // The store wins here and the outbound effect records it in the URL.
      if (folderParam == null) return;
    }

    if (folderParam === paramForFolderId(store.currentFolderId)) return;

    if (folderParam == null) {
      store.goToRoot();
    } else if (folderParam === UNASSIGNED_PARAM) {
      store.enterUnassigned();
    } else if (store.foldersById.has(folderParam)) {
      store.enterFolder(folderParam);
    } else {
      // Deleted folder, or a link from a workspace this user can't see. Land
      // them at the root; the outbound effect strips the dead param so they
      // don't re-share it.
      logger.warn('Folder deep link could not be resolved', {
        folderId: folderParam,
        showcaseId,
      });
      store.goToRoot();
    }
    // Deliberately NOT keyed on `currentFolderId`: this effect answers URL
    // changes only. Re-running it on a store-side move would read the URL as
    // it was *before* the outbound effect caught up, and an absent param
    // would then be taken as "go to root" — undoing the navigation the user
    // just made.
  }, [store, hydrated, folderParam, showcaseId]);

  // ─── Outbound: store → URL ───────────────────────────────────────────────
  useEffect(() => {
    if (reconciledFor.current !== showcaseId) return;

    // Deleting the folder you are standing in relocates you: the store's
    // `reconcileCurrentFolder` walks to the nearest surviving ancestor. That
    // move must REPLACE, not push — the entry it leaves behind names a folder
    // that no longer exists, so Back would land on a dead id, fail to resolve
    // it, and dump the user at the root instead of the parent they were just
    // moved to.
    const leavingDeletedFolder =
      folderParam != null &&
      folderParam !== UNASSIGNED_PARAM &&
      !store.foldersById.has(folderParam);
    const replace = mirroringExisting.current || leavingDeletedFolder;
    mirroringExisting.current = false;

    const nextFolder = paramForFolderId(store.currentFolderId);
    const showcaseIsStale =
      showcaseSlug != null && searchParams.get(SHOWCASE_PARAM) !== showcaseSlug;
    if (nextFolder === folderParam && !showcaseIsStale) return;

    write.current(
      prev => {
        const next = new URLSearchParams(prev);
        if (nextFolder == null) next.delete(FOLDER_PARAM);
        else next.set(FOLDER_PARAM, nextFolder);
        // The workspace rides along so the link resolves for a teammate whose
        // session has a different org selected.
        if (showcaseSlug != null) next.set(SHOWCASE_PARAM, showcaseSlug);
        return next;
      },
      // Pushing real moves is what makes browser Back walk back up the trail.
      { replace },
    );
  }, [
    store,
    hydrated,
    currentFolderId,
    folderParam,
    searchParams,
    showcaseId,
    showcaseSlug,
  ]);

  // Arriving on a `?folder=` link, the view would otherwise paint the root
  // until the structure lands and the inbound effect moves the store — a flash
  // of the wrong page. A failed load stops the wait: the root is all there is.
  const pending =
    folderParam != null &&
    consumedFor !== showcaseId &&
    store.loadFailedFor !== showcaseId &&
    !(hydrated && folderParam === paramForFolderId(currentFolderId));

  return { pending };
}
